import { isPlatformBrowser } from "@angular/common";
import { Router } from "@angular/router";
import { inject, Injectable, PLATFORM_ID, signal } from "@angular/core";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { catchError, firstValueFrom, of, shareReplay, take, tap, throwError } from "rxjs";

import { ILoginAsInternalUserPayload, ILoginResponse } from "@azm/core/interfaces/auth";
import { APIs } from "@azm/core/constants/api-names";
import { UserRoles } from "@azm/core/enums/user-roles";
import { HttpService } from "@azm/core/services/http";
import { ToastService } from "@azm/core/services/toast/toast.service";
import { DialogService } from "@azm/shared/components/overlay/dialog/services/dialog.service";

import { environment } from "@azm/environments/environment";
import { convertTokenJWT } from "../../utilities/convert-jwt";
import { splitPermissionCode, type PermissionInput } from "@azm/shared/enums/permissions";

@Injectable({
	providedIn: "root",
})
export class AuthService extends HttpService {
	protected override get controllerName(): string {
		return "auth/";
	}

	readonly #router = inject(Router);
	readonly #dialog = inject(DialogService);
	readonly #toast = inject(ToastService);
	readonly #platformId = inject(PLATFORM_ID);
	#crossTabLogoutInProgress = false;
	#crossTabLoginInProgress = false;
	#loginApplyFromStorageTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		super();
		if (isPlatformBrowser(this.#platformId)) {
			addEventListener("storage", this.#onStorage);
		}
	}

	#onStorage = (e: StorageEvent) => {
		if (e.storageArea !== localStorage) return;
		if (e.key !== environment.tokenPropertyName) return;
		if (e.newValue == null) {
			if (this.#loginApplyFromStorageTimer) {
				clearTimeout(this.#loginApplyFromStorageTimer);
				this.#loginApplyFromStorageTimer = null;
			}
			this.#applyLogoutFromAnotherTab();
			return;
		}
		this.#scheduleLoginSyncFromOtherTab(e.oldValue, e.newValue);
	};

	#scheduleLoginSyncFromOtherTab(oldValue: string | null, newValue: string) {
		if (this.#loginApplyFromStorageTimer) clearTimeout(this.#loginApplyFromStorageTimer);
		this.#loginApplyFromStorageTimer = setTimeout(() => {
			this.#loginApplyFromStorageTimer = null;
			if (localStorage.getItem(environment.tokenPropertyName) !== newValue) return;
			this.#applyLoginOrTokenFromOtherTab(oldValue, newValue);
		}, 0);
	}

	#sessionIdentityFromJwt(jwt: string | null) {
		if (!jwt) return null;
		const p = convertTokenJWT(jwt);
		if (!p) return null;
		const o = p as Record<string, unknown>;
		const u = o["unique_name"] ?? o["IdentityId"] ?? o["sub"];
		if (u == null || String(u).trim() === "") return null;
		return String(u).trim();
	}

	#applyLoginOrTokenFromOtherTab(oldValue: string | null, newValue: string) {
		if (this.#crossTabLoginInProgress) return;
		this.#crossTabLoginInProgress = true;
		try {
			const idOld = this.#sessionIdentityFromJwt(oldValue);
			const idNew = this.#sessionIdentityFromJwt(newValue);
			if (idOld && idNew && idOld === idNew) {
				return;
			}
			this.userPermissions.set([]);
			this.userPermissionsPerFeature.set(null);
			this.permissionsLoaded.set(false);
			this.#dialog.closeAll();
			void firstValueFrom(this.getUserPermissions().pipe(take(1)));
			this.#navigateToRoleShellHome();
		} finally {
			this.#crossTabLoginInProgress = false;
		}
	}

	#navigateToRoleShellHome() {
		if (this.isInternalUser) {
			void this.#router.navigate(["/dashboard"]);
			return;
		}
		const name = this.userRoleName;
		if (name) {
			void this.#router.navigate([`/${name}`]);
		} else {
			void this.#router.navigate(["/"]);
		}
	}

	#applyLogoutFromAnotherTab() {
		if (this.#crossTabLogoutInProgress) return;
		this.#crossTabLogoutInProgress = true;
		try {
			this.#dialog.closeAll();
			this.clear();
		} finally {
			this.#crossTabLogoutInProgress = false;
		}
	}

	get token() {
		return localStorage.getItem(environment.tokenPropertyName);
	}

	private set token(token: string | null) {
		if (token) {
			localStorage.setItem(environment.tokenPropertyName, token);
		} else {
			localStorage.removeItem(environment.tokenPropertyName);
		}
	}

	get refreshToken() {
		return localStorage.getItem(environment.refreshTokenPropertyName);
	}

	private set refreshToken(token: string | null) {
		if (token) {
			localStorage.setItem(environment.refreshTokenPropertyName, token);
		} else {
			localStorage.removeItem(environment.refreshTokenPropertyName);
		}
	}

	get userRole() {
		return localStorage.getItem(environment.userRolePropertyName) as number | null;
	}

	private set userRole(role: UserRoles | null) {
		if (role) {
			localStorage.setItem(environment.userRolePropertyName, `${role}`);
		} else {
			localStorage.removeItem(environment.userRolePropertyName);
		}
	}

	get userRoleName() {
		return this.userRole ? UserRoles[this.userRole].toLowerCase() : null;
	}

	get roleId(): number | null {
		const tokenData = convertTokenJWT();
		const rawRole = tokenData?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

		return rawRole ? Number(rawRole) : null;
	}

	get isAuthenticated() {
		return !!this.token;
	}

	get userIdentityId() {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;
		const rawUnique = tokenData["unique_name"];
		if (rawUnique != null && String(rawUnique).trim() !== "") return String(rawUnique).trim();
		const id = tokenData["IdentityId"];
		return id != null && String(id).trim() !== "" ? String(id).trim() : null;
	}

	get nationalIdentityIdFromToken(): string | null {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;
		const id = tokenData["IdentityId"];
		return id != null && String(id).trim() !== "" ? String(id).trim() : null;
	}

	get mobileNoFromToken(): string | null {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;
		const m = tokenData["MobileNo"];
		return m != null && String(m).trim() !== "" ? String(m).trim() : null;
	}

	get emailFromToken(): string | null {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;
		const e = tokenData["Email"];
		return e != null && String(e).trim() !== "" ? String(e).trim() : null;
	}

	get adUserNameFromToken(): string | null {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;
		const name = tokenData["ADUserName"];
		return name != null && String(name).trim() !== "" ? String(name).trim() : null;
	}

	get userIdFromToken(): string | null {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;
		const id = tokenData["sid"];
		return id != null && String(id).trim() !== "" ? String(id).trim() : null;
	}

	get firstNameFromToken(): string | null {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;
		const name = tokenData["FirstName"];
		return name != null && String(name).trim() !== "" ? String(name).trim() : null;
	}

	get displayNameFromToken(): string | null {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;

		const fullName = tokenData["FullName"];
		if (fullName != null && String(fullName).trim() !== "") return String(fullName).trim();

		const nameParts = ["FirstName", "FatherName", "GrandName", "FamilyName"]
			.map((key) => {
				const value = tokenData[key];
				return value != null ? String(value).trim() : "";
			})
			.filter(Boolean);
		if (nameParts.length > 0) return nameParts.join(" ");

		const displayName =
			tokenData["DisplayName"] ??
			tokenData["displayName"] ??
			tokenData["Name"] ??
			tokenData["UserName"] ??
			tokenData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
		if (displayName != null && String(displayName).trim() !== "") return String(displayName).trim();

		return null;
	}

	get headerDisplayLabel(): string {
		return this.displayNameFromToken ?? this.emailFromToken ?? this.userIdentityId ?? "";
	}

	matchesRequestApplicant(applicant: { identityId?: string | null; email?: string | null } | null | undefined): boolean {
		if (!applicant) return false;
		const aid = applicant.identityId?.trim();
		const aemail = applicant.email?.trim().toLowerCase();
		if (aid) {
			const uid = this.userIdentityId;
			if (uid && uid === aid) return true;
			const national = this.nationalIdentityIdFromToken;
			if (national && national === aid) return true;
		}
		if (aemail) {
			const te = this.emailFromToken?.trim().toLowerCase();
			if (te && te === aemail) return true;
		}
		return false;
	}

	get identityTypeIdFromToken(): number | null {
		const tokenData = convertTokenJWT() as Record<string, unknown> | undefined;
		if (!tokenData) return null;
		const raw = tokenData["IdentityTypeId"];
		if (raw == null || raw === "") return null;
		const n = Number(typeof raw === "string" ? raw.trim() : raw);
		return Number.isFinite(n) ? n : null;
	}

	get isInternalUser() {
		return true;
	}

	userPermissions = signal<string[]>([]);
	userPermissionsPerFeature = signal<Record<string, string[]> | null>(null);
	permissionsLoaded = signal(false);

	hasPermission(codeOrFeature: string, action?: string): boolean {
		const full = action ? `${codeOrFeature}.${action}` : codeOrFeature;
		return this.#hasExactPermission(full);
	}

	hasAnyPermission(codes: PermissionInput): boolean {
		const list = !codes ? [] : Array.isArray(codes) ? [...codes] : [codes];
		if (!list.length) return false;
		return list.some((code) => this.hasPermission(code));
	}

	hasAllPermissions(codes: readonly string[]): boolean {
		return codes.every((code) => this.hasPermission(code));
	}

	#hasExactPermission(full: string): boolean {
		if (this.userPermissions().includes(full)) return true;
		const parts = splitPermissionCode(full);
		if (!parts) return false;
		return !!this.userPermissionsPerFeature()?.[parts.feature]?.includes(parts.action);
	}

	getUserPermissions() {
		if (this.permissionsLoaded()) return of(this.userPermissions());

		return this.get<string[]>({ APIName: "permissions" }).pipe(
			catchError(() => of([] as string[])),
			tap((data) => {
				let permissions: Record<string, string[]> = {};
				data.forEach((permission) => {
					const [feature, action] = permission.split(".");
					if (!feature || !action) return;
					if (!permissions[feature]) permissions[feature] = [];
					permissions[feature].push(action);
				});

				this.userPermissions.set(data);
				this.userPermissionsPerFeature.set(data.length ? permissions : null);
				this.permissionsLoaded.set(true);
			}),
			shareReplay({ refCount: true, bufferSize: 1 }),
		);
	}

	loginAsInternalUser(body: ILoginAsInternalUserPayload) {
		this.userRole = UserRoles.Internal;

		return this.post<ILoginResponse>({
			APIName: APIs.auth.loginAsInternalUser,
			body,
		}).pipe(
			tap((data) => {
				this.#saveData(data);
			}),
		);
	}

	refreshTheToken() {
		return this.post<ILoginResponse>({
			APIName: APIs.auth.refreshToken,
			body: { refreshToken: this.refreshToken },
		}).pipe(
			tap((data) => {
				if (!data) {
					this.#dialog.closeAll();
					this.clear();
					return;
				}

				this.#saveData(data);
			}),
			catchError((error: HttpErrorResponse) => {
				if (error.status === HttpStatusCode.Unauthorized) {
					this.#dialog.closeAll();
					this.clear();
					return of(null);
				}

				return throwError(() => error);
			}),
		);
	}

	#saveData(data: ILoginResponse) {
		this.token = data.accessToken;
		this.refreshToken = data.refreshToken;
		this.userPermissions.set([]);
		this.userPermissionsPerFeature.set(null);
		this.permissionsLoaded.set(false);

		localStorage.removeItem(environment.mojLoginDataPropertyName);
		localStorage.setItem(environment.appTypePropertyName, btoa(environment.defaultAppType));
	}

	clearSessionOnly() {
		this.token = null;
		this.userRole = null;
		this.refreshToken = null;
		this.userPermissions.set([]);
		this.userPermissionsPerFeature.set(null);
		this.permissionsLoaded.set(false);
		localStorage.removeItem(environment.mojLoginDataPropertyName);
		localStorage.removeItem(environment.appTypePropertyName);
	}

	// User-initiated logout only (the header "Log out" action) — automatic session termination
	// (idle timeout, refresh-token failure) keeps calling clear() directly.
	async logoutUser(options?: { redirectUrl?: string }): Promise<void> {
		this.clear(options);
	}

	clear(options?: { redirectUrl?: string }) {
		this.#toast.clear();
		this.clearSessionOnly();
		if (environment.isLogoutRedirectUrl === true) {
			const url = environment.logoutRedirectUrl?.trim();
			if (url) {
				window.location.assign(url);
				return;
			}
		}
		const target = options?.redirectUrl ?? "/";
		void this.#router.navigateByUrl(target, { replaceUrl: true });
	}
}
